import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, CalendarPlus, CheckCheck, Inbox, Mail, MessageSquareText, Phone, Trash2 } from "lucide-react";
import { api, errMsg, mediaUrl, telHref } from "@/lib/api";
import { shopDateTime } from "@/lib/dates";
import { PageLoader } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Lightbox } from "@/components/site/Lightbox";
import { AdminLayout, btnSecondary, ConfirmDelete, Panel } from "@/components/admin/AdminLayout";
import { MessageBadges } from "@/pages/admin/MessagesPage";

const Row = ({ label, children, testId }) => (
  <div className="border-b border-zinc-800 py-3 last:border-0">
    <dt className="text-xs uppercase tracking-wider text-zinc-500">{label}</dt>
    <dd className="mt-0.5 whitespace-pre-line break-words text-base text-zinc-100" data-testid={testId}>{children || "—"}</dd>
  </div>
);

const contactBtn = "flex h-14 flex-1 items-center justify-center gap-2 rounded-md text-base font-semibold";

export default function MessageDetailPage() {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    api.get(`/admin/messages/${messageId}`).then((r) => setM(r.data)).catch(() => navigate("/admin/messages", { replace: true }));
  }, [messageId, navigate]);

  if (!m) return <PageLoader testId="message-loading" />;
  const setStatus = async (status) => {
    try {
      setM((await api.put(`/admin/messages/${m.id}`, { status })).data);
      toast.success(status === "Contacted" ? "Moved to Contacted" : status === "Archived" ? "Archived" : "Moved to New");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Message deleted");
      navigate(`/admin/messages?tab=${m.status}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const digits = m.phone.replace(/[^\d+]/g, "");

  return (
    <AdminLayout title={m.name} back={`/admin/messages?tab=${m.status}`} backLabel="Messages" testId="admin-message-detail-page">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="rounded-sm bg-zinc-800 px-2 py-0.5 text-xs font-semibold uppercase text-zinc-200" data-testid="message-status">{m.status}</span>
        <MessageBadges m={m} />
      </div>
      <div className="mb-6 flex gap-2" data-testid="message-contact-actions">
        <a href={telHref(m.phone)} className={`${contactBtn} bg-[#FFD23F] text-[#121214]`} data-testid="message-call-button"><Phone className="h-5 w-5" /> Call</a>
        <a href={`sms:${digits}`} className={`${contactBtn} border border-zinc-700 text-zinc-100`} data-testid="message-text-button"><MessageSquareText className="h-5 w-5" /> Text</a>
        {m.email && <a href={`mailto:${m.email}`} className={`${contactBtn} border border-zinc-700 text-zinc-100`} data-testid="message-email-button"><Mail className="h-5 w-5" /> Email</a>}
      </div>
      <Link to={`/admin/appointments/new?message=${m.id}`} className="mb-6 flex h-14 w-full items-center justify-center gap-2 rounded-md border-2 border-[#FFD23F] text-base font-semibold text-[#FFD23F] hover:bg-[#FFD23F]/10" data-testid="message-schedule-button">
        <CalendarPlus className="h-5 w-5" /> Schedule appointment
      </Link>
      <Panel testId="message-fields">
        <dl>
          <Row label="Received" testId="message-received">{shopDateTime(m.created_at)} (shop time)</Row>
          <Row label="Phone" testId="message-phone">{m.phone}</Row>
          <Row label="Email" testId="message-email">{m.email}</Row>
          <Row label="Best way to reach" testId="message-best-way">{m.best_way}</Row>
          <Row label="Vehicle" testId="message-vehicle">{m.vehicle}</Row>
          <Row label="What they need" testId="message-service">{m.service}</Row>
          <Row label="Message" testId="message-text">{m.message}</Row>
        </dl>
      </Panel>
      {m.photos.length > 0 && (
        <Panel title={`Photos (${m.photos.length})`} testId="message-photos">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {m.photos.map((p, i) => (
              <button key={i} onClick={() => setLightbox(i)} className="aspect-[4/3] overflow-hidden rounded-md bg-black" data-testid={`message-photo-${i}`}>
                <img src={mediaUrl(p.thumb_url)} alt={p.caption} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </Panel>
      )}
      {m.email_alert === "failed" && m.email_error && <p className="mb-4 text-sm text-zinc-500" data-testid="message-email-error">Email alert error: {m.email_error}</p>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {m.status === "New" && <Button onClick={() => setStatus("Contacted")} className={btnSecondary} data-testid="message-mark-contacted-button"><CheckCheck className="h-4 w-4" /> Mark contacted</Button>}
        {m.status !== "Archived" && <Button onClick={() => setStatus("Archived")} className={btnSecondary} data-testid="message-archive-button"><Archive className="h-4 w-4" /> Archive</Button>}
        {m.status !== "New" && <Button onClick={() => setStatus("New")} className={btnSecondary} data-testid="message-move-new-button"><Inbox className="h-4 w-4" /> Move back to New</Button>}
        <ConfirmDelete description="This message and its photos will be deleted for good." onConfirm={remove}
          trigger={<Button className={`${btnSecondary} text-[#ff8a80]`} data-testid="message-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>} />
      </div>
      {lightbox !== null && <Lightbox photos={m.photos} index={lightbox} onClose={closeLightbox} onIndex={setLightbox} />}
    </AdminLayout>
  );
}
